/**
 * @file Native app message interface.
 */

import canUseDOM from '../../util/can-use-dom';
import getLogger from '../../util/logger';

const log = getLogger('NativeAppsInterface');

/**
 * @enum NATIVE_APP_MESSAGE_HANDLER
 * @type {{CUSTOM_PRINT_CONFIG: string}}
 */
const NATIVE_APP_MESSAGE_HANDLER = {
  CUSTOM_PRINT_CONFIG: 'customPrintConfigurator',
};

/**
 * @enum NATIVE_APP_EVENT
 * @type {{GALLERY_CREATE_SUCCESS: string, CUSTOM_PRINT_CONFIG_DISMISS: string, CUSTOM_PRINT_CONFIG_ERROR: string, ALBUM_UPDATE_SUCCESS: string, CUSTOM_PRINT_CONFIG_COMPLETE: string, GALLERY_UPDATE_SUCCESS: string, ALBUM_CREATE_SUCCESS: string}}
 */
const NATIVE_APP_EVENT = {
  // Custom Prints
  CUSTOM_PRINT_CONFIG_COMPLETE: 'custom_print_config_complete',
  CUSTOM_PRINT_CONFIG_DISMISS: 'custom_print_config_dismiss',
  CUSTOM_PRINT_CONFIG_ERROR: 'custom_print_config_error',

  // Cards
  CARDS_CONFIG_COMPLETE: 'cards_config_complete',
  CARDS_CONFIG_DISMISS: 'cards_config_dismiss',
  CARDS_CONFIG_ERROR: 'cards_config_error',
  CARDS_CONFIG_CONTACTS_SELECT: 'cards_config_contacts_select',
  CARDS_CONFIG_CARD_ELEMENT_OPEN: 'cards_config_card_element_open',

  // PrintProducts
  PRINT_PRODUCT_CONFIG_COMPLETE: 'print_product_config_complete',
  PRINT_PRODUCT_CONFIG_DISMISS: 'print_product_config_dismiss',

  // albumCustomPrint
  ALBUM_CUSTOM_PRINT_CONFIG_COMPLETE: 'album_custom_print_config_complete',
  ALBUM_CUSTOM_PRINT_CONFIG_DISMISS: 'album_custom_print_config_dismiss',

  // Pro/Shutterbug Dashboard
  ALBUM_FOLDER_CREATE_SUCCESS: 'album_folder_create_success',
  ALBUM_FOLDER_CREATE_ERROR: 'album_folder_create_error',
  ALBUM_FOLDER_UPDATE_SUCCESS: 'album_folder_update_success',
  ALBUM_FOLDER_UPDATE_ERROR: 'album_folder_update_error',

  ALBUM_CREATE_SUCCESS: 'album_create_success',
  ALBUM_CREATE_PROGRESS: 'album_create_progress',
  ALBUM_CREATE_ERROR: 'album_create_error',
  ALBUM_UPDATE_SUCCESS: 'album_update_success',
  ALBUM_UPDATE_ERROR: 'album_update_error',

  // Onboarding
  ALBUM_FOLDER_ONBOARDING_REQUEST: 'album_folder_onboarding_request',

  // Other
  WEB_REQUEST_EMAIL_CLIENT: 'web_request_email_client',

  // Album Slideshow Viewer
  ALBUM_SLIDESHOW_VIEWER_DISMISS: 'album_slideshow_viewer_dismiss',
  ALBUM_SLIDESHOW_VIEWER_UPDATE_ERROR: 'album_slideshow_viewer_update_error',
  ALBUM_SLIDESHOW_VIEWER_SHARE_DIALOG: 'album_slideshow_viewer_share_dialog',
  ALBUM_SLIDESHOW_VIEWER_UPLOAD: 'album_slideshow_viewer_upload',

  // Album Slideshow Designer
  ALBUM_SLIDESHOW_DESIGNER_DISMISS: 'album_slideshow_designer_dismiss',
  ALBUM_SLIDESHOW_DESIGNER_UPDATE_ERROR:
    'album_slideshow_designer_update_error',
  ALBUM_SLIDESHOW_DESIGNER_SHARE_DIALOG:
    'album_slideshow_designer_share_dialog',
  ALBUM_SLIDESHOW_DESIGNER_UPLOAD_PHOTOS:
    'album_slideshow_designer_upload_photos',

  //  Photo Attribution Page
  PHOTO_ATTRIBUTION_PAGE_DISMISS: 'photo_attribution_page_dismiss',

  // Marketing Materials Configurator
  MARKETING_MATERIAL_CONFIGURATOR_COMPLETE:
    'marketing_material_configurator_complete',
  MARKETING_MATERIAL_CONFIGURATOR_UPSELL_REQUIRED:
    'marketing_material_configurator_upsell_required',
  MARKETING_MATERIAL_CONFIGURATOR_FAILURE:
    'marketing_material_configurator_failure',

  // Album Intro Layout Designer
  ALBUM_INTRO_LAYOUT_DESIGNER_DISMISS: 'album_intro_layout_designer_dismiss',
  ALBUM_INTRO_LAYOUT_DESIGNER_UPDATE_ERROR:
    'album_intro_layout_designer_update_error',

  // Consumer Feedback
  CONSUMER_FEEDBACK_DISMISS: 'consumer_feedback_dismiss',

  // Wedding's create flow:
  WEDDING_EVENT_CONFIGURATION_COMPLETE: 'wedding_event_configuration_complete',

  // Freemium's create flow:
  FREEMIUM_EVENT_CONFIGURATION_COMPLETE:
    'freemium_event_configuration_complete',

  // Album Slideshow
  ALBUM_SLIDESHOW_UPDATE_SUCCESS: 'album_slideshow_update_success',
  ALBUM_SLIDESHOW_UPDATE_ERROR: 'album_slideshow_update_error',

  // Freemium Event tutorial config
  FREEMIUM_EVENT_TUTORIAL_CONFIG_STEP_CHANGE:
    'freemium_event_tutorial_config_step_change',
  FREEMIUM_EVENT_TUTORIAL_CONFIG_IN_STEP_PURCHASE:
    'freemium_event_tutorial_config_in_step_purchase',
  FREEMIUM_EVENT_TUTORIAL_CONFIG_EVENT_ADDON_ITEM_PURCHASE:
    'freemium_event_tutorial_config_event_addon_item_purchase',
  ASSET_DOWNLOAD_EVENT: 'asset_download_event',

  // Circles
  CIRCLES_CREATE_DIALOG: 'circles_create_dialog',
};

/**
 * Attempts to determine whether the app runs inside a WebView, either iOS or Android.
 *
 * @param {boolean} [defaultValue] - Default return value before all data is available for proper determination (i.e. Server-side rendering where there's no window object).
 * @returns {boolean}
 */
const isNativeAppWebView = defaultValue => {
  if (!canUseDOM) {
    return defaultValue;
  }

  const iosMessageHandler = window?.webkit?.messageHandlers;
  const androidMessageHandler = window?.androidInterface;

  return (
    !!window?.APP_PLATFORM &&
    (window?.APP_NAME || '').toLowerCase().includes('waldo') &&
    (!!iosMessageHandler || !!androidMessageHandler)
  );
};

/**
 * Attempts to determine whether the app runs inside an iOS WebView
 *
 * @param {boolean} [defaultValue] - Default return value before all data is available for proper determination (i.e. Server-side rendering where there's no window object).
 * @returns {boolean}
 */
const isIOSNativeAppWebView = defaultValue => {
  return window?.APP_PLATFORM === `IOS` && isNativeAppWebView(defaultValue);
};

/**
 * Posts a message on native app bridges (iOS / Android).
 *
 * @param {Object} event - The event/message to pass through.
 * @param {Object} [data] - Optional data.
 *
 * @returns {undefined}
 */
const postNativeAppEventMessage = (event, data) => {
  if (!window) {
    return undefined;
  }

  const iosMessageHandler = window?.webkit?.messageHandlers?.web;
  const androidMessageHandler = window?.androidInterface?.web;

  if (iosMessageHandler) {
    // Ensures that we pass plain java script and avoids random
    // methods/functions/DOM objects to be passed down:
    const parsedData = data ? JSON.parse(JSON.stringify(data)) : undefined;

    log.info(
      `postNativeAppEventMessage :: Calling window.webkit.messageHandlers.web.postMessage ::`,
      { event, data: parsedData },
    );

    try {
      window.webkit.messageHandlers.web.postMessage({
        event,
        data: parsedData,
      });
    } catch (error) {
      log.error(`postNativeAppEventMessage :: ${error.message}`, error);
    }
  }

  if (androidMessageHandler) {
    const payload = JSON.stringify({ event, data });

    log.info(
      `postNativeAppEventMessage :: Calling window.androidInterface.web :: ${payload}`,
    );

    try {
      window.androidInterface.web(payload);
    } catch (error) {
      log.error(`postNativeAppEventMessage :: ${error.message}`, error);
    }
  }

  return undefined;
};

/**
 * Posts a message on native app bridges (iOS / Android) for custom print config only.
 *
 * @param {Object} event - The event/message to pass through.
 * @param {string} [message] - Optional message.
 *
 * @returns {undefined}
 */
const postNativeAppCustomPrintConfigEventMessage = (event, message) => {
  if (!window) {
    return undefined;
  }

  const iosMessageHandler = window?.webkit?.messageHandlers;
  const androidMessageHandler = window?.androidInterface;

  if (iosMessageHandler && iosMessageHandler.customPrintConfigurator) {
    log.info(
      `postNativeAppEventMessage :: Calling window.webkit.messageHandlers.customPrintConfigurator.postMessage ::`,
      { event, message },
    );

    try {
      window.webkit.messageHandlers.customPrintConfigurator.postMessage({
        event,
        message,
      });
    } catch (error) {
      log.error(`postNativeAppEventMessage :: ${error.message}`, error);
    }
  }

  if (androidMessageHandler) {
    const payload = JSON.stringify({ event, message });

    log.info(
      `postNativeAppEventMessage :: Calling window.androidInterface.customPrintConfigurator :: ${payload}`,
    );

    try {
      window.androidInterface.customPrintConfigurator(payload);
    } catch (error) {
      log.error(`postNativeAppEventMessage :: ${error.message}`, error);
    }
  }

  return undefined;
};

/**
 * Posts custom print configurator complete event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppCustomConfiguratorComplete = () => {
  // Back compatibility for older iOS / Android clients that do not support
  // the new version of `postNativeAppEventMessage`.
  postNativeAppCustomPrintConfigEventMessage(
    NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_COMPLETE,
  );

  postNativeAppEventMessage(NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_COMPLETE);
};

/**
 * Posts custom print configurator dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppCustomConfiguratorDismiss = () => {
  // Back compatibility for older iOS / Android clients that do not support
  // the new version of `postNativeAppEventMessage`.
  postNativeAppCustomPrintConfigEventMessage(
    NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_DISMISS,
  );

  postNativeAppEventMessage(NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_DISMISS);
};

/**
 * Posts custom print configurator error event in native clients.
 *
 * @param {string} message - The error message.
 * @returns {undefined}
 */
const postNativeAppCustomConfiguratorError = message => {
  // Back compatibility for older iOS / Android clients that do not support
  // the new version of `postNativeAppEventMessage`.
  postNativeAppCustomPrintConfigEventMessage(
    NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_ERROR,
    message,
  );

  postNativeAppEventMessage(NATIVE_APP_EVENT.CUSTOM_PRINT_CONFIG_ERROR, {
    error: message,
  });
};

/**
 * Posts cards configurator complete event in native clients.
 *
 * @param {string} accountCardId - The AccountCard RecordID.
 * @returns {undefined}
 */
const postNativeAppCardsConfiguratorComplete = accountCardId => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CARDS_CONFIG_COMPLETE, {
    accountCardId,
  });
};

/**
 * Posts custom print configurator dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppCardsConfiguratorDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CARDS_CONFIG_DISMISS);
};

/**
 * Posts cards configurator error event in native clients.
 *
 * @param {string} message - The error message.
 * @returns {undefined}
 */
const postNativeAppCardsConfiguratorError = message => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CARDS_CONFIG_ERROR, {
    error: message,
  });
};

/**
 * Posts cards configurator contacts select event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppCardsContactsSelect = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CARDS_CONFIG_CONTACTS_SELECT);
};

/**
 * Posts cards configurator card element open in native clients.
 *
 * @param {Object} options The options.
 * @param {string} options.accountCardId The AccountCard RecordID.
 * @param {string} options.cardElementId The CardElement RecordID.
 * @param {CARD_ELEMENT_TYPE} options.cardElementType The CardElementType.
 * @param {Object} [options.cardElementConfiguration] The current CardElementConfiguration.
 * @returns {undefined}
 */
const postNativeAppCardsCardElementOpen = ({
  accountCardId,
  cardElementId,
  cardElementType,
  cardElementConfiguration,
}) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CARDS_CONFIG_CARD_ELEMENT_OPEN, {
    accountCardId,
    cardElementId,
    cardElementType,
    cardElementConfiguration,
  });
};

/**
 * Posts PrintProduct complete event in native clients.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The AccountCard.
 * @param {Object} options.albumCustomPrint The global PRINT_PRODUCT AlbumCustomPrint.
 * @param {Object} options.printProduct The PrintProduct.
 * @returns {undefined}
 */
const postNativeAppPrintProductConfiguratorComplete = ({
  accountCard,
  albumCustomPrint,
  printProduct,
}) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.PRINT_PRODUCT_CONFIG_COMPLETE, {
    accountCard,
    albumCustomPrint,
    printProduct,
  });
};

/**
 * Posts PrintProduct configurator dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppPrintProductConfiguratorDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.PRINT_PRODUCT_CONFIG_DISMISS);
};

/**
 * Posts AlbumCustomPrint complete event in native clients.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The AccountCard.
 * @param {Object} options.albumCustomPrint The AlbumCustomPrint.
 * @returns {undefined}
 */
const postNativeAppAlbumCustomPrintConfiguratorComplete = ({
  accountCard,
  albumCustomPrint,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_CUSTOM_PRINT_CONFIG_COMPLETE,
    {
      accountCard,
      albumCustomPrint,
    },
  );
};

/**
 * Posts AlbumCustomPrint configurator dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppAlbumCustomPrintConfiguratorDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_CUSTOM_PRINT_CONFIG_DISMISS);
};

/**
 * Posts album folder create success event in native clients.
 *
 * @param {Object} opts - The options
 * @param {string} opts.albumFolderId - The album folder id.
 * @param {string} opts.eventItemId - the event item id
 *
 * @returns {undefined}
 */
const postNativeAppAlbumFolderCreateSuccess = ({
  albumFolderId,
  eventItemId,
}) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_FOLDER_CREATE_SUCCESS, {
    albumFolderId,
    eventItemId,
  });
};

/**
 * Posts album folder create error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumFolderCreateError = error => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_FOLDER_CREATE_ERROR, {
    error,
  });
};

/**
 * Posts album folder update success event in native clients.
 *
 * @param {string} albumFolderId - The album folder id.
 * @returns {undefined}
 */
const postNativeAppAlbumFolderUpdateSuccess = albumFolderId => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_FOLDER_UPDATE_SUCCESS, {
    albumFolderId,
  });
};

/**
 * Posts album folder update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumFolderUpdateError = error => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_FOLDER_UPDATE_ERROR, {
    error,
  });
};

/**
 * Posts album create success event in native clients.
 *
 * @param {Object} opts - Options to be passed to native.
 * @param {string} opts.albumId - The album id.
 * @param {string?} opts.contributorAssignmentId - The contributor assignment Id (if any).
 * @param {Object?} opts.albumData - An object containing relevant album data for mobile clients.
 * @param {Object?} opts.albumFolderUpdateInput - Deprecated, use values: Ready to use AlbumFolderUpdateInput object.
 * @param {Object?} opts.specialtyAlbumsUpdateInput - Deprecated, use values:  Ready to use object with AlbumUpdateInput objects for each specialty album.
 * @param {Object?} opts.values - Ready to use object with AlbumUpdateInput objects for each specialty album.
 * @returns {undefined}
 */
const postNativeAppAlbumCreateSuccess = ({
  albumId,
  contributorAssignmentId,
  albumData,
  albumFolderUpdateInput,
  specialtyAlbumsUpdateInput,
  values,
}) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_CREATE_SUCCESS, {
    albumId,
    contributorAssignmentId,
    albumFolderUpdateInput,
    specialtyAlbumsUpdateInput,
    albumData,
    values,
  });
};

/**
 * Posts album create progress event in native clients.
 *
 * @param {Object} opts - Options to be passed to native.
 * @param {string} opts.albumId - The album id.
 * @returns {undefined}
 */
const postNativeAppAlbumCreateProgress = ({ albumId }) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_CREATE_PROGRESS, {
    albumId,
  });
};

/**
 * Posts album create error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumCreateError = error => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_CREATE_ERROR, {
    error,
  });
};

/**
 * Posts album update success event in native clients.
 *
 * @param {string} albumId - The Album id.
 * @returns {undefined}
 */
const postNativeAppAlbumUpdateSuccess = albumId => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_UPDATE_SUCCESS, {
    albumId,
  });
};

/**
 * Posts album update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumUpdateError = error => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_UPDATE_ERROR, {
    error,
  });
};

/**
 * Posts email client request.
 *
 * @param {Object} data The data.
 * @param {string} data.email_address The email address.
 * @param {string} data.subject The email subject.
 * @param {string} data.body The email body.
 * @returns {undefined}
 */
const postNativeEmailClientRequest = data => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.WEB_REQUEST_EMAIL_CLIENT, {
    ...data,
  });
};

/**
 * Posts AlbumFolder onboarding request in native clients.
 *
 * @param {Object} options The options.
 * @param {string} options.accountId The account id making the request.
 * @param {Array<Object>} [options.joinItems] The Album join items.
 * @param {Object} [options.albumFolderJoinItem] The AlbumFolder join item in case the AlbumFolder is JOIN_ALL.
 * @returns {undefined}
 */
const postNativeAppAlbumFolderOnboardingRequest = ({
  accountId,
  joinItems = [],
  albumFolderJoinItem,
}) => {
  const data = {
    albums_join_by_code_input: joinItems.length
      ? joinItems.map(item => ({
          account_id: accountId,
          identities: [item.identity_id],
          join_codes: item.join_codes,
        }))
      : null,
    join_by_code_input: albumFolderJoinItem.join_code
      ? {
          account_id: accountId,
          join_code: albumFolderJoinItem.join_code,
          identities: albumFolderJoinItem.identities,
          album_folder_join: true,
        }
      : null,
  };

  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_FOLDER_ONBOARDING_REQUEST,
    data,
  );
};

/**
 * Posts Album Slideshow Viewer dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowViewerDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_SLIDESHOW_VIEWER_DISMISS);
};

/**
 * Posts Album Slideshow Viewer update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowViewerUpdateError = error => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_SLIDESHOW_VIEWER_UPDATE_ERROR,
    {
      error,
    },
  );
};

/**
 * Posts Album Slideshow Viewer share dialog event in native clients.
 *
 * @param {string} albumId - The album id
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowViewerShareDialog = albumId => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_SLIDESHOW_VIEWER_SHARE_DIALOG,
    {
      albumId,
    },
  );
};

/**
 * Posts Album Slideshow Viewer upload event in native clients.
 *
 * @param {string} albumId - The album id
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowViewerUpload = albumId => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_SLIDESHOW_VIEWER_UPLOAD, {
    albumId,
  });
};

/**
 * Posts Album Slideshow Designer dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowDesignerDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_SLIDESHOW_DESIGNER_DISMISS);
};

/**
 * Posts Album Slideshow Designer update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowDesignerUpdateError = error => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_SLIDESHOW_DESIGNER_UPDATE_ERROR,
    {
      error,
    },
  );
};

/**
 * Posts Album Slideshow Designer share dialog event in native clients.
 *
 * @param {string} albumId - The album id
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowDesignerShareDialog = albumId => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_SLIDESHOW_DESIGNER_SHARE_DIALOG,
    {
      albumId,
    },
  );
};

/**
 * Posts Album Slideshow Designer upload photos event in native clients.
 *
 * @param {string} albumId - The album id
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowDesignerUploadPhotos = albumId => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_SLIDESHOW_DESIGNER_UPLOAD_PHOTOS,
    {
      albumId,
    },
  );
};

/**
 * Posts Weddings Business Card dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppPhotoAttributionPageDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.PHOTO_ATTRIBUTION_PAGE_DISMISS);
};

/**
 * Posts Marketing material configuration event in native clients
 *
 * @param {Object} opts Object of available options.
 * @param {String} opts.albumFolderId The albumFolderId that just completed the configuration.
 * @param {String} opts.albumFolderName The albumFolderName that just completed the configuration.
 * @param {String} opts.albumSubtype The albumSubtype of the album folder that just completed the configuration.
 * @returns {undefined}
 */
const postNativeAppMarketingMaterialConfigurationComplete = ({
  albumFolderId,
  albumFolderName,
  albumSubtype,
}) => {
  const data = { albumFolderId, albumFolderName, albumSubtype };

  postNativeAppEventMessage(
    NATIVE_APP_EVENT.MARKETING_MATERIAL_CONFIGURATOR_COMPLETE,
    data,
  );
};

/**
 * Posts Marketing material configuration event in native clients
 *
 * @param {Object} error The error that caused a failure
 * @returns {undefined}
 */
const postNativeAppMarketingMaterialConfigurationFailure = error => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.MARKETING_MATERIAL_CONFIGURATOR_FAILURE,
    error,
  );
};

/**
 * Posts Marketing material configuration event in native clients
 *
 * @param {Object} opts Object of available options.
 * @param {String} opts.albumFolderId The albumFolderId that just completed the configuration.
 * @param {String} opts.albumFolderName The albumFolderName that just completed the configuration.
 * @param {String} opts.albumSubtype The albumSubtype of the album folder that just completed the configuration.
 * @returns {undefined}
 */
const postNativeAppMarketingMaterialConfigurationUpsellRequired = ({
  albumFolderId,
  albumFolderName,
  albumSubtype,
}) => {
  const data = { albumFolderId, albumFolderName, albumSubtype };

  postNativeAppEventMessage(
    NATIVE_APP_EVENT.MARKETING_MATERIAL_CONFIGURATOR_UPSELL_REQUIRED,
    data,
  );
};

/**
 * Posts Intro Layout Designer dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppAlbumIntroLayoutDesignerDismiss = () => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_INTRO_LAYOUT_DESIGNER_DISMISS,
  );
};

/**
 * Posts Album Intro Layout Designer update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumIntroLayoutDesignerUpdateError = error => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.ALBUM_INTRO_LAYOUT_DESIGNER_UPDATE_ERROR,
    {
      error,
    },
  );
};

/**
 * Posts Consumer Feedback dismiss event in native clients.
 *
 * @returns {undefined}
 */
const postNativeAppConsumerFeedbackDismiss = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CONSUMER_FEEDBACK_DISMISS);
};

/**
 * Posts the values & selected event item ID of a wedding event after configuration has been
 * completed.
 *
 * @param {string} albumFolderId - The album folder id.
 * @returns {undefined}
 */
const postNativeAppWeddingEventConfigurationComplete = ({
  values,
  eventItemId,
  eventItem,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.WEDDING_EVENT_CONFIGURATION_COMPLETE,
    {
      values,
      eventItemId,
      eventItem,
    },
  );
};

/**
 * Posts the values & selected event addon item IDs of a wedding event after configuration has been
 * completed.
 *
 * @param {Object} opts - the options
 * @param {Object} opts.values - the current configuration
 * @param {Array} opts.eventAddonItemIds - The selected event addon item ids
 * @param {Array} opts.eventAddonItems - The selected event addon items
 * @param {String} opts.albumId - the album id
 * @param {string} opts.albumFolderId - The album folder id.
 * @param {Object} opts.searchSubscriptionItem - The selected subscription item
 *
 * @returns {undefined}
 */
const postNativeAppFreemiumEventConfigurationComplete = ({
  values,
  eventAddonItemIds,
  eventAddonItems,
  albumId,
  albumFolderId,
  searchSubscriptionItem,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.FREEMIUM_EVENT_CONFIGURATION_COMPLETE,
    {
      values,
      eventAddonItemIds,
      eventAddonItems,
      albumId,
      albumFolderId,
      searchSubscriptionItem,
    },
  );
};

/**
 * Posts Album Slideshow update success event in native clients.
 *
 * @param {Object} opts Object of available options.
 * @param {string} opts.albumId - The album id
 * @param {Object} opts.slideshow - the updated slideshow's content
 *
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowUpdateSuccess = ({ albumId, slideshow }) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_SLIDESHOW_UPDATE_SUCCESS, {
    albumId,
    slideshow,
  });
};

/**
 * Posts Album Slideshow update error event in native clients.
 *
 * @param {string} error - The error message.
 * @returns {undefined}
 */
const postNativeAppAlbumSlideshowUpdateError = error => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ALBUM_SLIDESHOW_UPDATE_ERROR, {
    error,
  });
};

/**
 * Posts freemium event tutorial step change event in native clients.
 *
 * @param {Object} opts Object of available options.
 * @param {string} opts.entityId - The album or albumFolder ID
 * @param {Object} opts.stepKeys - The array of steps in the wizard
 * @param {String} opts.activeStepKey - Key of active step.
 * @param {Boolean} opts.isComplete - Whether or not the wizard was completed.
 *
 * @returns {undefined}
 */
const postNativeAppFreemiumEventTutorialConfigStepChange = ({
  entityId,
  stepKeys,
  activeStepKey,
  isComplete,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.FREEMIUM_EVENT_TUTORIAL_CONFIG_STEP_CHANGE,
    {
      entityId,
      stepKeys,
      activeStepKey,
      isComplete,
    },
  );
};

/**
 * Posts freemium event tutorial step change event in native clients.
 *
 * @param {Object} opts Object of available options.
 * @param {String} opts.entityId - The album or albumFolder ID
 * @param {String} opts.eventAddonItemId - The event addon item the user would be purchasing
 * @param {String} opts.eventAddonItemType - The event addon item type user would be purchasing
 * @param {String} opts.featureId - The waldofied feature id
 *
 * @returns {undefined}
 */
const postNativeAppFreemiumEventTutorialConfigInStepPurchase = ({
  entityId,
  eventAddonItemId,
  eventAddonItemType,
  featureId,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.FREEMIUM_EVENT_TUTORIAL_CONFIG_IN_STEP_PURCHASE,
    {
      entityId,
      eventAddonItemId,
      eventAddonItemType,
      featureId,
    },
  );
};

/**
 * Posts freemium event tutorial step change event in native clients.
 *
 * @param {Object} opts Object of available options.
 * @param {String} opts.entityId - The album or albumFolder ID
 * @param {String} opts.eventAddonItemId - The event addon item the user would be purchasing
 * @param {String} opts.eventAddonItemType - The event addon item type user would be purchasing
 *
 * @returns {undefined}
 */
const postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase = ({
  entityId,
  eventAddonItemId,
  eventAddonItemType,
}) => {
  postNativeAppEventMessage(
    NATIVE_APP_EVENT.FREEMIUM_EVENT_TUTORIAL_CONFIG_EVENT_ADDON_ITEM_PURCHASE,
    {
      entityId,
      eventAddonItemId,
      eventAddonItemType,
    },
  );
};

/**
 * Posts circles create dialog event in native clients
 *
 * @returns {undefined}
 */
const postNativeAppCirclesCreateDialog = () => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.CIRCLES_CREATE_DIALOG);
};
/**
 * Posts asset download event in native clients.
 *
 * @param {Object} opts Object of available options.
 * @param {String} opts.downloadUrl - The download URL
 *
 * @returns {undefined}
 */
const postNativeAppAssetDownload = ({ downloadUrl }) => {
  postNativeAppEventMessage(NATIVE_APP_EVENT.ASSET_DOWNLOAD_EVENT, {
    downloadUrl,
  });
};

export {
  NATIVE_APP_MESSAGE_HANDLER,
  NATIVE_APP_EVENT,
  isNativeAppWebView,
  postNativeAppEventMessage,
  postNativeAppCustomConfiguratorComplete,
  postNativeAppCustomConfiguratorDismiss,
  postNativeAppCustomConfiguratorError,
  postNativeAppCardsConfiguratorComplete,
  postNativeAppCardsConfiguratorDismiss,
  postNativeAppCardsConfiguratorError,
  postNativeAppCardsContactsSelect,
  postNativeAppCardsCardElementOpen,
  postNativeAppAlbumFolderCreateSuccess,
  postNativeAppAlbumFolderCreateError,
  postNativeAppAlbumFolderUpdateSuccess,
  postNativeAppAlbumFolderUpdateError,
  postNativeAppAlbumCreateSuccess,
  postNativeAppAlbumCreateError,
  postNativeAppAlbumUpdateSuccess,
  postNativeAppAlbumUpdateError,
  postNativeAppAlbumFolderOnboardingRequest,
  postNativeEmailClientRequest,
  postNativeAppAlbumCreateProgress,
  postNativeAppPrintProductConfiguratorDismiss,
  postNativeAppPrintProductConfiguratorComplete,
  postNativeAppAlbumCustomPrintConfiguratorDismiss,
  postNativeAppAlbumCustomPrintConfiguratorComplete,
  postNativeAppAlbumSlideshowViewerDismiss,
  postNativeAppAlbumSlideshowViewerUpdateError,
  postNativeAppAlbumSlideshowViewerShareDialog,
  postNativeAppAlbumSlideshowViewerUpload,
  postNativeAppAlbumSlideshowDesignerDismiss,
  postNativeAppAlbumSlideshowDesignerUpdateError,
  postNativeAppAlbumSlideshowDesignerShareDialog,
  postNativeAppAlbumSlideshowDesignerUploadPhotos,
  postNativeAppPhotoAttributionPageDismiss,
  postNativeAppMarketingMaterialConfigurationFailure,
  postNativeAppMarketingMaterialConfigurationComplete,
  postNativeAppMarketingMaterialConfigurationUpsellRequired,
  postNativeAppAlbumIntroLayoutDesignerDismiss,
  postNativeAppAlbumIntroLayoutDesignerUpdateError,
  postNativeAppConsumerFeedbackDismiss,
  postNativeAppWeddingEventConfigurationComplete,
  postNativeAppFreemiumEventConfigurationComplete,
  postNativeAppAlbumSlideshowUpdateSuccess,
  postNativeAppAlbumSlideshowUpdateError,
  postNativeAppFreemiumEventTutorialConfigStepChange,
  postNativeAppFreemiumEventTutorialConfigInStepPurchase,
  postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase,
  postNativeAppCirclesCreateDialog,
  postNativeAppAssetDownload,
  isIOSNativeAppWebView,
};
